import * as Sentry from '@sentry/react';

/**
 * Wraps an async function with Sentry span tracking
 * 
 * @param operation - The operation name for the span (e.g., "api.fetch", "ui.click")
 * @param name - Descriptive name for the span
 * @param fn - The async function to execute
 * @returns The result of the async function
 */
export async function withSentrySpan<T>(
  operation: string,
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: operation,
      name: name,
    },
    async () => {
      return await fn();
    }
  );
}

/**
 * Captures an exception with additional context
 * 
 * @param error - The error to capture
 * @param context - Additional context to attach to the error
 */
export function captureExceptionWithContext(
  error: Error | unknown,
  context: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    user?: { id?: string; email?: string; username?: string };
    level?: Sentry.SeverityLevel;
  }
): string {
  return Sentry.captureException(error, {
    tags: context.tags,
    extra: context.extra,
    user: context.user,
    level: context.level
  });
}

/**
 * Sets user context for Sentry
 * 
 * @param user - User information to set in Sentry context
 */
export function setSentryUser(user: {
  id?: string;
  username?: string;
  email?: string;
  companyCode?: string;
  apiSystem?: 'smart' | 'sws';
} | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      email: user.email,
      // Additional custom attributes
      ...(user.companyCode && { company_code: user.companyCode }),
      ...(user.apiSystem && { api_system: user.apiSystem })
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Adds breadcrumb for navigation tracking
 * 
 * @param message - The breadcrumb message
 * @param data - Additional data to attach
 */
export function addNavigationBreadcrumb(
  message: string,
  data?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message,
    level: 'info',
    data
  });
}

/**
 * Adds breadcrumb for user actions
 * 
 * @param action - The action performed
 * @param data - Additional data about the action
 */
export function addUserActionBreadcrumb(
  action: string,
  data?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: action,
    level: 'info',
    data
  });
}

/**
 * Creates a Sentry logger instance
 * 
 * @returns Sentry logger instance
 */
export function getSentryLogger() {
  const { logger } = Sentry;
  return logger;
}

/**
 * Wraps a click handler with Sentry span tracking
 * 
 * @param buttonName - Name of the button for tracking
 * @param handler - The click handler function
 * @returns Wrapped click handler
 */
export function trackButtonClick<T extends (...args: any[]) => void>(
  buttonName: string,
  handler: T
): T {
  return ((...args: Parameters<T>) => {
    Sentry.startSpan(
      {
        op: "ui.click",
        name: buttonName,
      },
      () => {
        addUserActionBreadcrumb(`Clicked ${buttonName}`);
        return handler(...args);
      }
    );
  }) as T;
}

/**
 * Tracks API call performance and errors
 * 
 * @param endpoint - The API endpoint being called
 * @param method - HTTP method
 * @param fn - The fetch function to execute
 * @returns The API response
 */
export async function trackApiCall<T>(
  endpoint: string,
  method: string,
  fn: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: "http.client",
      name: `${method} ${endpoint}`,
    },
    async (span) => {
      try {
        const startTime = Date.now();
        const result = await fn();
        const duration = Date.now() - startTime;
        
        span?.setAttribute("http.method", method);
        span?.setAttribute("http.url", endpoint);
        span?.setAttribute("http.duration_ms", duration);
        span?.setStatus({ code: 1 }); // OK status
        
        return result;
      } catch (error) {
        span?.setStatus({ code: 2 }); // Error status
        span?.setAttribute("error", true);
        span?.setAttribute("error.message", error instanceof Error ? error.message : String(error));
        throw error;
      }
    }
  );
}

/**
 * Measures the performance of a function
 * 
 * @param name - Name of the operation being measured
 * @param fn - The function to measure
 * @returns The result of the function
 */
export async function measurePerformance<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: "function",
      name: name,
    },
    async (span) => {
      const startTime = performance.now();
      try {
        const result = await fn();
        const duration = performance.now() - startTime;
        span?.setAttribute("duration_ms", duration);
        return result;
      } catch (error) {
        span?.setAttribute("error", true);
        throw error;
      }
    }
  );
}